<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ReceiptText, ChevronRight } from 'lucide-vue-next'
import { Alert, Badge, Button, Card, Input, Spinner } from '@/components/ui'
import SetupFlowShell from '@/components/shared/SetupFlowShell.vue'
import { resolveInvoiceAccess } from '@/api/authClient'
import { track } from '@/lib/usageTracker'
import { useAuthStore } from '@/stores/authStore'

// --- Interface-design checkpoint ---
// Intent: "I have an invoice" recovery path. The user has selected a
// store with no existing Lyt Accounting integration. They must enter
// an invoice number; the backend validates the Lyt Accounting add-on.
// Palette: paper/ink, stamp-red for denial, receipt-green for
// approval. Provenance chips carry the store context into the dialog.

const router = useRouter()
const store = useAuthStore()

const invoiceNumber = ref('')
const loading = ref(false)
const apiError = ref(false)
const networkError = ref(false)
const denied = ref(false)
const denyReason = ref('This invoice is not eligible for the Lyt Accounting add-on.')

const contextItems = computed(() => [
  {
    label: 'Acting context',
    value: store.user?.email ? `Signed in as ${store.user.email}` : 'No user',
    tone: store.user?.email ? 'success' : 'warning',
  },
  {
    label: 'Store',
    value: store.selectedStore?.storeName
      ? `${store.selectedStore.storeName} (${store.selectedStore.storeDb})`
      : 'Not selected',
    tone: store.selectedStore ? 'default' : 'warning',
  },
  {
    label: 'Integration',
    value: 'Not configured for this store',
    tone: 'warning',
  },
])

function resetUI() {
  loading.value = false
  apiError.value = false
  networkError.value = false
  denied.value = false
}

async function submit() {
  track('form:InvoiceAccess', 'submit')
  resetUI()
  if (!store.user || !store.selectedStore) {
    router.replace({ name: 'StoreSelection' })
    return
  }
  const trimmed = invoiceNumber.value.trim()
  if (!trimmed) {
    apiError.value = true
    return
  }
  try {
    loading.value = true
    const response = await resolveInvoiceAccess({
      userId: store.user.id,
      storeId: store.selectedStore.storeId,
      storeDb: store.selectedStore.storeDb,
      invoiceNumber: trimmed,
    })
    if (response.approved) {
      store.setQueryParams({ invoiceNumber: trimmed, storeID: store.selectedStore.storeId })
      if (response.authRedirectUrl) {
        window.location.href = response.authRedirectUrl
        return
      }
      router.replace({ name: 'LoginProviderSelection' })
    } else {
      denied.value = true
      denyReason.value = response.reason || denyReason.value
    }
  } catch (e) {
    const msg = String((e as Error)?.message || e)
    if (/failed to fetch|network|TypeError/i.test(msg)) {
      networkError.value = true
    } else if (/403|HTTP\s*403/i.test(msg)) {
      denied.value = true
      denyReason.value = 'This invoice is not eligible for the Lyt Accounting add-on.'
    } else {
      apiError.value = true
      console.error(e)
    }
  } finally {
    loading.value = false
  }
}

function back() {
  router.replace({ name: 'StoreSelection' })
}
</script>

<template>
  <SetupFlowShell
    eyebrow="Invoice recovery"
    title="I have an invoice"
    subtitle="This store is not connected to a Lyt Accounting integration. Enter the invoice reference from your subscription to verify the Lyt Accounting add-on and continue."
    :current-step="3"
    :steps="['Sign in', 'Choose store', 'Connect', 'Workspace']"
    :context-items="contextItems"
  >
    <Card padding="lg" class="invoice-panel">
      <div class="invoice-panel__actions">
        <Button variant="secondary" id="backToStores" @click="() => { track('btn:BackToStores', 'click'); back() }">Back to stores</Button>
      </div>

      <div class="invoice-panel__provenance">
        <Badge variant="muted">{{ store.selectedStore?.storeName ?? 'Store' }}</Badge>
        <Badge variant="muted">DB {{ store.selectedStore?.storeDb ?? '-' }}</Badge>
        <Badge variant="muted">Group {{ store.selectedStore?.group ?? '-' }}</Badge>
      </div>

      <form class="invoice-panel__form" @submit.prevent="submit">
        <div class="invoice-panel__field">
          <label for="invoiceNumber" class="invoice-panel__label">Invoice number</label>
          <div class="invoice-panel__input">
            <ReceiptText :size="16" aria-hidden="true" />
            <Input
              id="invoiceNumber"
              v-model="invoiceNumber"
              placeholder="e.g. 3393083"
              required
            />
          </div>
          <p class="invoice-panel__hint">
            Use the invoice number from your Lyt subscription confirmation. The backend validates
            the Lyt Accounting add-on before allowing provider auth to proceed.
          </p>
        </div>

        <div class="invoice-panel__actions">
          <Button type="submit" id="verifyInvoice" :disabled="loading">
            <Spinner v-if="loading" size="sm" />
            <span v-if="!loading">Verify invoice and continue</span>
            <span v-else>Verifying</span>
            <ChevronRight v-if="!loading" :size="14" />
          </Button>
        </div>
      </form>

      <Alert
        v-if="denied"
        id="invoiceDenied"
        variant="error"
        title="Invoice not eligible"
      >
        {{ denyReason }}
        <p class="invoice-panel__denial-help">
          If you believe this is incorrect, contact
          <a href="mailto:support@chrilantech.com" class="support-link">support@chrilantech.com</a>.
        </p>
      </Alert>

      <Alert
        v-else-if="networkError"
        id="invoiceNetworkError"
        variant="warning"
        title="Verification service unreachable"
      >
        We couldn't reach the verification service. Check your connection and try again.
        <div class="invoice-panel__alert-actions">
          <Button id="retryInvoiceNetwork" @click="() => { track('btn:RetryInvoiceAccess', 'click'); submit() }">Retry</Button>
        </div>
      </Alert>

      <Alert
        v-else-if="apiError"
        id="invoiceApiError"
        variant="error"
        title="Verification failed"
      >
        We could not verify that invoice. Double-check the number and retry.
        <div class="invoice-panel__alert-actions">
          <Button id="retryInvoice" @click="() => { track('btn:RetryInvoiceAccess', 'click'); submit() }">Try again</Button>
        </div>
      </Alert>
    </Card>
  </SetupFlowShell>
</template>

<style scoped>
.invoice-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.invoice-panel__actions {
  display: flex;
  justify-content: flex-end;
}

.invoice-panel__actions :deep(button) {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.invoice-panel__label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.invoice-panel__provenance {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.invoice-panel__form {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface-muted);
  border-radius: var(--radius-sm);
  padding: 1rem;
}

.invoice-panel__field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.invoice-panel__input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.invoice-panel__hint {
  margin: 0;
  font-size: 0.82rem;
  color: var(--color-ink-soft);
}

.invoice-panel__alert-actions {
  margin-top: 0.6rem;
}

.invoice-panel__denial-help {
  margin: 0.4rem 0 0;
  font-size: 0.85rem;
  color: var(--color-ink-soft);
}

.support-link {
  color: var(--color-primary);
  text-decoration: underline;
}

@media (max-width: 640px) {
  .invoice-panel__actions :deep(button) {
    width: 100%;
    justify-content: center;
  }
}
</style>
